import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Card,
  Row,
  Col,
  Pagination,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import {
  User,
  CreateUserDto,
  UpdateUserDto,
  UserListParams,
} from "../../types";
import { UserService, OrgService } from "../../services";
import { Org } from "../../types/org";
import ErrorHandler from "../../utils/errorHandler";
import "./UserList.scss";

const { Option } = Select;

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    limit: 10,
    total: 0,
  });

  // 查询参数
  const [searchParams, setSearchParams] = useState<UserListParams>({});

  // 角色列表
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);

  // 组织列表
  const [orgs, setOrgs] = useState<Org[]>([]);

  // 使用 ref 跟踪是否正在加载，防止重复请求
  const isLoadingRef = useRef(false);

  // 使用 ref 跟踪组织列表是否已加载，防止重复请求
  const orgsLoadedRef = useRef(false);

  // 使用 ref 跟踪角色列表是否已加载，防止重复请求
  const rolesLoadedRef = useRef(false);

  // 加载角色列表
  const loadRoles = async () => {
    // 如果已经加载过，直接返回
    if (rolesLoadedRef.current) {
      return;
    }

    try {
      const response = await UserService.getRolesList();
      if (response.code === 0 && response.data) {
        setRoles(response.data.roles);
        rolesLoadedRef.current = true;
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    }
  };

  // 加载组织列表
  const loadOrgs = async () => {
    // 如果已经加载过或者已有数据，直接返回
    if (orgsLoadedRef.current || orgs.length > 0) {
      return;
    }

    // 标记为正在加载，防止并发请求
    orgsLoadedRef.current = true;

    try {
      const response = await OrgService.getOrgList({ limit: 1000 }); // 获取所有组织
      if (response.code === 0 && response.data) {
        setOrgs(response.data.data || []);
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      // 请求失败时重置标记，允许重试
      orgsLoadedRef.current = false;
    }
  };

  // 加载用户列表
  const loadUsers = async (params?: UserListParams) => {
    setLoading(true);
    try {
      const queryParams = {
        ...searchParams,
        ...params,
        page: pagination.current,
        limit: pagination.limit,
      };

      const response = await UserService.getUserList(queryParams);

      if (response.code === 0 && response.data) {
        // 根据后端返回的数据结构调整
        const data = response.data;
        setUsers(data.list || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取用户列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadUsers();
    loadRoles(); // 加载角色列表
    loadOrgs(); // 加载组织列表
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 搜索用户
  const handleSearch = async (values: any) => {
    const params: UserListParams = {
      ...values,
      page: 1, // 搜索时重置到第一页
      limit: pagination.limit,
    };

    // 更新搜索参数状态（用于分页时使用）
    setSearchParams(params);

    // 直接调用 API 加载数据
    await loadUsersWithParams(params);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    const resetParams = {};
    setSearchParams(resetParams);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // 使用独立函数，绕过状态合并问题
    loadUsersWithParams({ page: 1, limit: pagination.limit });
  };

  // 直接使用参数加载用户，不合并状态
  const loadUsersWithParams = async (params: any) => {
    // 如果正在加载，直接返回，防止重复请求
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    try {
      const response = await UserService.getUserList(params);

      if (response.code === 0 && response.data) {
        // 根据后端返回的数据结构调整
        const data = response.data;
        setUsers(data.list || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取用户列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // 打开编辑弹窗
  const handleEdit = async (user: User) => {
    try {
      // 获取用户详细信息
      const response = await UserService.getUserDetail(user.id);
      if (response.code === 0 && response.data) {
        setCurrentUser(response.data);
        form.setFieldsValue(response.data);
        setEditModalVisible(true);
      } else {
        message.error(response.msg || "获取用户信息失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    }
  };

  // 创建新用户
  const handleCreate = () => {
    setCurrentUser(null);
    form.resetFields();
    form.setFieldsValue({ role: "customer", isEnabled: true });
    setEditModalVisible(true);
  };

  // 保存用户
  const handleSave = async (values: CreateUserDto | UpdateUserDto) => {
    try {
      setLoading(true);
      let response;

      if (currentUser) {
        // 更新用户
        response = await UserService.updateUser(
          currentUser.id,
          values as UpdateUserDto
        );
      } else {
        // 创建用户
        response = await UserService.createUser(values as CreateUserDto);
      }

      if (response.code === 0) {
        message.success(currentUser ? "用户更新成功" : "用户创建成功");
        setEditModalVisible(false);
        loadUsers();
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 分页变化 - 只调用 api/account 接口，不触发其他接口
  const handlePaginationChange = (page: number, limit?: number) => {
    const newLimit = limit || pagination.limit;
    const isSizeChange = limit !== undefined && limit !== pagination.limit;
    const targetPage = isSizeChange ? 1 : page; // 改变每页数量时重置到第一页

    // 合并搜索参数和分页参数，只调用用户列表接口（api/account）
    const params = {
      ...searchParams,
      page: targetPage,
      limit: newLimit,
    };

    // 直接调用 loadUsersWithParams，只请求 api/account 接口
    // loadUsersWithParams 内部会更新 pagination 状态，不需要在这里更新
    loadUsersWithParams(params);
  };

  // 复制邀请码到剪切板
  const handleCopyIdentifier = async (identifier: string) => {
    try {
      await navigator.clipboard.writeText(identifier);
      message.success("邀请码已复制到剪切板");
    } catch (error) {
      // 降级方案：使用传统方法复制
      const textArea = document.createElement("textarea");
      textArea.value = identifier;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        message.success("邀请码已复制到剪切板");
      } catch (fallbackError) {
        message.error("复制失败，请手动复制");
      }
      document.body.removeChild(textArea);
    }
  };

  // 根据角色值获取角色标签
  const getRoleLabel = (roleValue: string) => {
    const roleItem = roles.find((role) => role.value === roleValue);
    return roleItem ? roleItem.label : roleValue;
  };

  // 表格列定义
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 50,
    },
    {
      title: "手机号",
      dataIndex: "phone",
      key: "phone",
      width: 120,
    },
    {
      title: "用户名",
      dataIndex: "username",
      key: "username",
      width: 100,
    },
    {
      title: "角色",
      dataIndex: "role",
      key: "role",
      width: 100,
      render: (role: string) => getRoleLabel(role),
    },
    {
      title: "归属组织",
      dataIndex: "org",
      key: "org",
      width: 150,
      render: (org: any) => (org && org.name ? org.name : "-"),
    },
    {
      title: "识别码（邀请码）",
      dataIndex: "identifier",
      key: "identifier",
      width: 150,
      render: (identifier: string) => (
        <Space>
          <span>{identifier}</span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyIdentifier(identifier)}
            title="复制邀请码"
          />
        </Space>
      ),
    },
    {
      title: "状态",
      dataIndex: "isEnabled",
      key: "isEnabled",
      width: 80,
      render: (isEnabled: boolean) => (
        <span style={{ color: isEnabled ? "#52c41a" : "#ff4d4f" }}>
          {isEnabled ? "启用" : "禁用"}
        </span>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (date: string) => date,
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="user-list">
      {/* 搜索区域 */}
      <Card className="search-card" style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="phone" label="手机号">
            <Input placeholder="请输入手机号" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="role" label="角色">
            <Select placeholder="请选择角色" style={{ width: 120 }} allowClear>
              {roles.map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
              >
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                新建用户
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 用户列表 */}
      <Card title="用户列表">
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1000 }}
        />

        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.limit}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }
            onChange={handlePaginationChange}
          />
        </div>
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title={currentUser ? "编辑用户" : "新建用户"}
        open={editModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: "请输入用户名" }]}
              >
                <Input placeholder="请输入用户名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="手机号"
                rules={[
                  { required: true, message: "请输入手机号" },
                  { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号" },
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="orgId" label="归属组织">
                <Select placeholder="请选择归属组织" allowClear>
                  {orgs.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="邮箱"
                rules={[{ type: "email", message: "请输入正确的邮箱格式" }]}
              >
                <Input placeholder="请输入邮箱" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: "请选择角色" }]}
              >
                <Select placeholder="请选择角色">
                  {roles.map((role) => (
                    <Option key={role.value} value={role.value}>
                      {role.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <Input placeholder="请输入备注" />
          </Form.Item>

          <Form.Item name="isEnabled" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserList;
